'use client';
import React, { useEffect, useState } from 'react';

interface LuxGaugeProps {
  currentLux: number;
  targetLux: number;
  minLux: number;
  maxLux: number;
  percentage: number;
  status: 'good' | 'warning' | 'poor' | 'excess';
}

export default function LuxGauge({ currentLux, targetLux, percentage, status }: LuxGaugeProps) {
  const [animatedPct, setAnimatedPct] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedPct(Math.min(percentage, 150));
    }, 200);
    return () => clearTimeout(timer);
  }, [percentage]);

  const statusColors = {
    good: { stroke: '#22C55E', text: '#15803D', label: 'مناسبة' },
    warning: { stroke: '#F59E0B', text: '#B45309', label: 'تحتاج تحسين' },
    poor: { stroke: '#EF4444', text: '#B91C1C', label: 'غير كافية' },
    excess: { stroke: '#3B82F6', text: '#1D4ED8', label: 'أعلى من المطلوب' },
  };

  const sc = statusColors[status];

  // SVG arc parameters
  const SIZE = 240;
  const CX = SIZE / 2;
  const CY = SIZE / 2 + 20;
  const R = 90;
  const START_ANGLE = -210; // degrees
  const END_ANGLE = 30; // degrees
  const TOTAL_ANGLE = END_ANGLE - START_ANGLE; // 240 degrees

  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const polarToCartesian = (angle: number) => ({
    x: CX + R * Math.cos(toRad(angle)),
    y: CY + R * Math.sin(toRad(angle)),
  });

  const describeArc = (startAngle: number, endAngle: number) => {
    const start = polarToCartesian(startAngle);
    const end = polarToCartesian(endAngle);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${R} ${R} 0 ${largeArc} 1 ${end.x} ${end.y}`;
  };

  // Clamp percentage for arc drawing: 0–150 maps to START_ANGLE to END_ANGLE
  const clampedPct = Math.min(Math.max(animatedPct, 0), 150);
  const fillAngle = START_ANGLE + (clampedPct / 150) * TOTAL_ANGLE;

  // Zone arcs: poor 0-60%, warning 60-90%, good 90-110%, excess 110-150%
  const pctToAngle = (pct: number) => START_ANGLE + (pct / 150) * TOTAL_ANGLE;

  const zones = [
    { from: 0, to: 60, color: '#FCA5A5' },
    { from: 60, to: 90, color: '#FDE68A' },
    { from: 90, to: 110, color: '#BBF7D0' },
    { from: 110, to: 150, color: '#BFDBFE' },
  ];

  // Needle
  const needleAngle = START_ANGLE + (clampedPct / 150) * TOTAL_ANGLE;
  const needleLength = R - 10;
  const needleEnd = {
    x: CX + needleLength * Math.cos(toRad(needleAngle)),
    y: CY + needleLength * Math.sin(toRad(needleAngle)),
  };

  return (
    <div className="flex flex-col items-center w-full">
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full max-w-[260px]">
        {/* Zone arcs */}
        {zones.map((zone) => (
          <path
            key={`zone-${zone.from}-${zone.to}`}
            d={describeArc(pctToAngle(zone.from), pctToAngle(zone.to))}
            fill="none"
            stroke={zone.color}
            strokeWidth="14"
            strokeLinecap="round"
          />
        ))}

        {/* Active fill arc */}
        {clampedPct > 0 && (
          <path
            d={describeArc(START_ANGLE, fillAngle)}
            fill="none"
            stroke={sc.stroke}
            strokeWidth="10"
            strokeLinecap="round"
            style={{ transition: 'all 1s ease' }}
          />
        )}

        {/* Needle */}
        <line
          x1={CX}
          y1={CY}
          x2={needleEnd.x}
          y2={needleEnd.y}
          stroke="#3F4044"
          strokeWidth="3"
          strokeLinecap="round"
          style={{ transition: 'all 1s ease' }}
        />
        <circle cx={CX} cy={CY} r={7} fill="#3F4044" />
        <circle cx={CX} cy={CY} r={4} fill="white" />

        {/* Center text */}
        <text x={CX} y={CY - 20} textAnchor="middle" fontSize="26" fontWeight="800" fill={sc.stroke} fontFamily="Cairo, sans-serif">
          {Math.round(currentLux)}
        </text>
        <text x={CX} y={CY - 2} textAnchor="middle" fontSize="11" fill="#6B7280" fontFamily="Cairo, sans-serif">
          Lux
        </text>

        {/* Zone labels */}
        <text x={CX - 78} y={CY + 30} textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="Cairo, sans-serif">ضعيف</text>
        <text x={CX} y={CY + 50} textAnchor="middle" fontSize="9" fill="#22C55E" fontFamily="Cairo, sans-serif">مناسب</text>
        <text x={CX + 78} y={CY + 30} textAnchor="middle" fontSize="9" fill="#9CA3AF" fontFamily="Cairo, sans-serif">مرتفع</text>
      </svg>

      {/* Stats below gauge */}
      <div className="flex gap-6 mt-2 text-center">
        <div>
          <div className="text-xs text-muted-foreground">الحالي</div>
          <div className="font-bold font-tabular" style={{ color: sc.stroke }}>{Math.round(currentLux)} Lux</div>
        </div>
        <div className="w-px bg-border" />
        <div>
          <div className="text-xs text-muted-foreground">المستهدف</div>
          <div className="font-bold font-tabular text-foreground">{targetLux} Lux</div>
        </div>
        <div className="w-px bg-border" />
        <div>
          <div className="text-xs text-muted-foreground">النسبة</div>
          <div className="font-bold font-tabular" style={{ color: sc.stroke }}>{Math.min(percentage, 150)}%</div>
        </div>
      </div>

      <div className="mt-3 px-4 py-2 rounded-full text-sm font-bold" style={{ backgroundColor: sc.stroke + '20', color: sc.text }}>
        {sc.label}
      </div>
    </div>
  );
}